import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, Button, DialogActions } from '@mui/material';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import { IoCloseCircleOutline } from 'react-icons/io5';
import {
  FormField2,
  IDynamicFormValue,
  IMultiSelectControlField2
} from 'src/lib';
import { useFormik } from 'formik';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useMatomoFunction from 'src/hooks/use-matomo-function.hook';
import { AlertDialog, DialogResult } from 'src/components/analytics';
import _ from 'lodash';
import { getTableColumnData } from '../../../../../../components/collapsible-table/table-column-prep';
import { getDataForSimulatedRows } from '../../../../../../components/collapsible-table/table-data-prep';
import { IFilterObj } from '../../../../../../components/filters';
import { transformDynamicFormValueToFilterPayload } from '../../../../../../components/filters/utils/filter-form-utils';
import {
  useAppDispatch,
  useAppSelector,
  useDialogVisibilityHook
} from '../../../../../../hooks';
import useModuleMetadata from '../../../../../../hooks/use-module-metadata.hook';
import getOutputTableColumn from '../../../common/features/table/components/output-column-prep';
import { setTableMetaData } from '../../../common/features/table/store/table-slice2';
import MODULE_TO_PAGE_MAP from '../../../home/types/simulation-module-to-page-map';
import {
  InputType,
  MODIFIED_ROW_INDICATOR_KEY,
  TableComponent,
  TypeOfTableData
} from '../../../pricing-simulator/types/generic-table.model';
import KPISelection from '../../../pricing-simulator/types/kpi-selection.enum';
import SaveAsContainer from '../../../components/save-as-container/save-as-container';
import OverrideNameDialog from '../../../pricing-simulator/components/save-scenario-dialog/override-name-dialog/override-name-dialog';
import SimulationTableContainer from '../../../pricing-simulator/components/simulation-table-container/simulation-table-container';
import {
  getLocalFiltersData,
  getFilterFieldsForCogs,
  getTableDataForCogs,
  resetCogsSimulationData
} from '../../store/planner-slice-async-thunks';
import { IDynamicFilterFormFields } from '../../../home/types/scenario-form.model';
import {
  clearCogsInputs,
  handleCogsSimulationFlagValue,
  updateFilterFieldOptions
} from '../../store/planner-slice';
import CogsFilter from './cogs-filter/cogs-filter';
import TableSkeletonContainer from '../../../pricing-simulator/components/simulation-table-container/table-skeleton';
import CogsTableComponent from '../cogs-table/cogs-table';
import CogsFooter from './cogs-footer';
import { MatomoEventTrackMapObject } from '../../../constants/constant';
import { filterColumnsForToggleView } from '../../../pricing-simulator/utils/column-utils';
import getInnovationPayload from '../../../innovation/utils/filter-utils';
import { validateCogsFilters } from './utils';
import { ConfigDiffFinder } from '../../../common/utils';

function CogsDialog(props: {
  isCogsDialogOpen: boolean;
  closeCogsDialog: any;
  //  onClose: any
}) {
  const appDispatch = useAppDispatch();
  const { pricing: pricingAccessConfig } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );
  const { callFunction } = useMatomoFunction();

  const [columns, setColumns] = useState([]);

  // local state for alert dialog
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const [currentActiveDialog, setActiveDialog] = useState('');
  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const dialogMessageObj = {
    submitDialog:
      'This will clear all the simulation in all the modules and reset all input values. This action cannot be undone. Are you sure you want to proceed?',
    resetDialog:
      'This will clear all the simulation in all the modules and reset all input values. This action cannot be undone. Are you sure you want to proceed?',
    clearDialog:
      'This will clear all the values and restore the table to default view. This action cannot be undone. Are you sure you want to proceed?'
  };

  const dialogTitleObj = {
    submitDialog: 'Submit',
    resetDialog: 'Reset',
    applyDialog: 'Apply'
  };

  const {
    kpiSelection,
    pricingSimulationData,
    fetchingPricingSimulationData,
    toggleData,
    enableSimulation
  } = useAppSelector((state) => state.pricingSimulator);

  const {
    fetchingCogsTableData,
    cogsTableData,
    isClearClicked,
    baseCogsTableData,
    topCogsFiltersValue,
    topCogsFiltersDefaultValue
  } = useAppSelector((state) => state.plannerV2);

  const {
    filters: filterFields,
    initialValues: filterInitialValues,
    combinations
  } = topCogsFiltersValue;

  const [openOverrideDialog, setOverrideDialogValue] = useState(false);
  // const [isCogsDialogOpen, setIsCogsDialogOpen] = useState(false);
  const { tableMetadaData, baseTableMetadata } = useAppSelector(
    (state) => state.table2
  );

  const kpiModuleID = JSON.parse(localStorage.getItem('moduleIdData'))?.filter(
    (e) => e.name === 'kpi_update'
  )[0].pk_id;

  const [cogsFilters, setCogsFilters] = useState([]);
  // local state for applied cogs filters
  const [appliedCogsFilters, setAppliedCogsFilters] = useState(
    {} as { [key: string]: { label: string; value: string }[] }
  );

  // local state to check are cogs filters valid or not
  const [areCogsFiltersValid, setAreCogsFiltersValid] = useState(false);

  // local state to check cogs filters are changed or not
  const [areCogsFiltersChanged, setAreCogsFiltersChanged] = useState(false);

  // local state to set apply button enabled
  const [isApplyEnabled, setApplyEnabled] = useState(false);

  // local state for enabling/disabling submit and clear button
  const [isSubmitCogsEnabled, setSubmitCogsEnabled] = useState(false);
  const [isClearEnabled, setClearEnabled] = useState(false);

  const { moduleId, sourceId, scenarioId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.PLANNER
  });

  const [externalReset, setExternalReset] = useState(false);

  const getPageName = () => {
    const page_title = window.location.href.split('/').at(-1);
    return page_title;
  };

  const getColumnsData = (tableResData: any) => {
    const { inputCols, resultCols, response } = tableResData;
    appDispatch(setTableMetaData({ tableData: response }));
    setColumns([
      ...getTableColumnData(
        inputCols,
        TableComponent.COGS,
        response?.header,
        [],
        !pricingAccessConfig.provide_inputs
      ),
      ...getOutputTableColumn(
        resultCols,
        response?.output_toggle,
        response?.footer.row,
        {},
        TableComponent.COGS,
        response.metadata
      )
    ]);
  };

  useEffect(() => {
    appDispatch(
      getFilterFieldsForCogs({
        sourceID: sourceId,
        moduleId: kpiModuleID,
        scenarioId: Number(scenarioId)
      })
    ).then((response) => {
      const { values, fields } = response?.payload as {
        values: IDynamicFormValue;
        fields: FormField2[];
      };
    });
    appDispatch(
      getTableDataForCogs({
        dataInput: {
          filter_inputs: getInnovationPayload(
            filterInitialValues,
            topCogsFiltersDefaultValue
          ),
          row: []
        },
        sourceId,
        moduleId: kpiModuleID,
        scenarioId
      })
    ).then((res) => {
      getColumnsData(res.payload);
    });
  }, []);

  // on load, setting the appliedCogsFilters as initial values of filters
  useEffect(() => {
    if (!_.isEmpty(topCogsFiltersValue.initialValues)) {
      setAppliedCogsFilters(topCogsFiltersValue.initialValues);
    }
  }, [topCogsFiltersValue.initialValues]);

  // handler for setting are filters changed
  const setDefaultFilters = (filters) => {
    setAreCogsFiltersChanged(
      ConfigDiffFinder.isObjectDifferent(filters.values, appliedCogsFilters)
    );
  };

  // hanlder for filters on change
  const handleFilterChange = (filters) => {
    // checking if filters are changed as compared to applied filters
    const areFiltersChanged = ConfigDiffFinder.isObjectDifferent(
      filters.values,
      appliedCogsFilters
    );
    // updating the areCogsFiltersChanged state
    setAreCogsFiltersChanged(areFiltersChanged);

    // on change of filters, updating isApplyEnabled state to true
    setApplyEnabled(true);

    // updating the state for areCogsFiltersValid as per filters current state
    setAreCogsFiltersValid(validateCogsFilters(filters));
  };

  // function for cogs filters apply click
  const handleFilterApply = (filters) => {
    // updating the appliedCogsFilters state
    setAppliedCogsFilters(filters);
    // setting isApplyEnabled state to false
    setApplyEnabled(false);
    // calling get table api with updated filters
    appDispatch(
      getTableDataForCogs({
        dataInput: {
          filter_inputs: getInnovationPayload(
            filters,
            topCogsFiltersDefaultValue
          ),
          row: []
        },
        sourceId,
        moduleId: kpiModuleID,
        scenarioId
      })
    ).then((res) => {
      getColumnsData(res.payload);
    });
  };

  // handler for filter reset and resetting the table data with all filters
  const handleFilterReset = () => {
    setAppliedCogsFilters(filterInitialValues);
    setApplyEnabled(false);
    appDispatch(
      getTableDataForCogs({
        dataInput: {
          filter_inputs: getInnovationPayload(
            filterInitialValues,
            topCogsFiltersDefaultValue
          ),
          row: []
        },
        sourceId,
        moduleId: kpiModuleID,
        scenarioId
      })
    ).then((res) => {
      getColumnsData(res.payload);
    });
  };

  const getChangedInputRows = () => {
    const idColumns: string[] = tableMetadaData.header
      .filter(
        (data) =>
          !data.parent.is_visible &&
          data.parent.field_format === InputType.NUMBER
      )
      .sort((a, b) => a.parent.level - b.parent.level)
      .map((data) => data.parent.parent_field);
    const simulatedData = getDataForSimulatedRows(
      tableMetadaData,
      idColumns,
      MODIFIED_ROW_INDICATOR_KEY.COGS_IS_EDITED,
      MODIFIED_ROW_INDICATOR_KEY.COGS_IS_NOT_DEFAULT,
      MODIFIED_ROW_INDICATOR_KEY.COGS_IS_USER_INPUT
    );
    if (!simulatedData.inputs.length && !simulatedData.data.length) {
      return {
        isChanged: false,
        row: null
      };
    }
    return {
      isChanged: true,
      row: simulatedData
    };
  };

  /**
   * Handler for simulation reset
   * Simply set the simulation id to null and refetch all the data again
   */
  const handleSimulationReset = () => {
    setExternalReset(true);
    setTimeout(() => {
      setExternalReset(false);
    }, 2000);
    appDispatch(
      resetCogsSimulationData({
        sourceId,
        moduleId: kpiModuleID,
        scenarioId,
        filters: getInnovationPayload(
          appliedCogsFilters,
          topCogsFiltersDefaultValue
        )
      })
    ).then((res) => {
      getColumnsData(res.payload);
    });
  };

  const handleSimulationClick = () => {
    callFunction(
      MatomoEventTrackMapObject.track_simulate_button_click.event_key,
      getPageName()
    );
    const result = getChangedInputRows();
    if (!result.isChanged) {
      return;
    }
    const dataPoints = {
      filter_inputs: getInnovationPayload(
        appliedCogsFilters,
        topCogsFiltersDefaultValue
      ),
      row: result.row
    };

    appDispatch(handleCogsSimulationFlagValue());
    appDispatch(
      getTableDataForCogs({
        dataInput: dataPoints,
        sourceId,
        moduleId: kpiModuleID,
        scenarioId
      })
    ).then((res) => {
      getColumnsData(res.payload);
    });
  };

  /**
   * handler for clear inputs on cogs table
   */
  const resetCogsInputs = () => {
    const response = baseCogsTableData;

    const resultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      response.header,
      ''
    );
    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.INPUT
      ],
      response.header,
      ''
    );

    const clearedTableData = { response, resultCols, inputCols };

    getColumnsData(clearedTableData);
  };

  // confirmation dialog function for apply, reset & submit
  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES && currentActiveDialog === 'submitDialog') {
      handleSimulationClick();
    }
    if (result === DialogResult.YES && currentActiveDialog === 'resetDialog') {
      handleSimulationReset();
    }
    if (result === DialogResult.YES && currentActiveDialog === 'clearDialog') {
      resetCogsInputs();
    }

    setConfirmationDialogOpen(false);
  };

  const handleFilterResetAction = (dialogType) => {
    setActiveDialog(dialogType);
    setDialogTitle(dialogTitleObj[dialogType]);
    setDialogMessage(dialogMessageObj[dialogType]);
    setConfirmationDialogOpen(true);
  };

  const handleSubmitAction = (dialogType) => {
    setActiveDialog(dialogType);
    setDialogTitle(dialogTitleObj[dialogType]);
    setDialogMessage(dialogMessageObj[dialogType]);
    setConfirmationDialogOpen(true);
  };
  const handleResetAction = (dialogType): void => {
    setActiveDialog(dialogType);
    setDialogTitle(dialogTitleObj[dialogType]);
    setDialogMessage(dialogMessageObj[dialogType]);
    setConfirmationDialogOpen(true);
  };

  const handleClearAction = (dialogType): void => {
    setActiveDialog(dialogType);
    setDialogTitle(dialogTitleObj[dialogType]);
    setDialogMessage(dialogMessageObj[dialogType]);
    setConfirmationDialogOpen(true);
  };

  // checking for changes to table state
  useEffect(() => {
    if (!_.isEqual(tableMetadaData, baseTableMetadata)) {
      setSubmitCogsEnabled(true);
      setClearEnabled(true);
    } else {
      setSubmitCogsEnabled(false);
      setClearEnabled(false);
    }
  }, [tableMetadaData]);

  return (
    <>
      <Dialog
        fullWidth
        maxWidth='xl'
        // open={isCogsDialogOpen}
        open={props.isCogsDialogOpen}
        disableEscapeKeyDown
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        // onClose={handleDialogClose}
      >
        <Grid
          container
          justifyContent='space-between'
          sx={{ padding: '8px 16px 0px 16px' }}
        >
          <Grid item xs='auto'>
            <Typography color='primary' fontWeight={700}>
              Update Cogs
            </Typography>
          </Grid>
          <Grid item xs='auto'>
            <IconButton
              aria-label='close'
              size='medium'
              color='primary'
              // onClick={props.handleCogsDialogVisibility}
              onClick={props.closeCogsDialog}
            >
              <IoCloseCircleOutline />
            </IconButton>
          </Grid>
        </Grid>
        <DialogContent sx={{ padding: '0 24px' }}>
          <>
            <CogsFilter
              filterValue={cogsFilters}
              setDefaultFilters={setDefaultFilters}
              handleFilterChange={handleFilterChange}
              handleFilterApply={handleFilterApply}
              handleFilterReset={handleFilterReset}
              areCogsFiltersValid={areCogsFiltersValid}
              areCogsFiltersChanged={areCogsFiltersChanged}
              isApplyEnabled={isApplyEnabled}
              appliedCogsFilters={appliedCogsFilters}
            />
            <Grid container style={{ marginTop: '20px' }}>
              <Grid item xs={12}>
                <Grid container>
                  {(fetchingCogsTableData || !columns.length) && (
                    <Grid item xs={12}>
                      <TableSkeletonContainer rowLength={4} headerLength={6} />
                    </Grid>
                  )}
                  {!fetchingCogsTableData && columns.length > 0 && (
                    <Grid item xs={12}>
                      <CogsTableComponent columns={columns} />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              {/* footer, will integrate with footer component then */}
            </Grid>
          </>
        </DialogContent>
        <DialogActions>
          <CogsFooter
            onSubmitClick={() => handleSubmitAction('submitDialog')}
            onResetClick={() => handleResetAction('resetDialog')}
            onClearClick={() => handleClearAction('clearDialog')}
            isTableDataFetched={fetchingCogsTableData}
            closeCogs={props.closeCogsDialog}
            isSubmitCogsEnabled={isSubmitCogsEnabled}
            isClearEnabled={isClearEnabled}
          />
        </DialogActions>
      </Dialog>
      <AlertDialog
        closeHandler={handleConfirmationDialogClose}
        isDialogOpen={isConfirmationDialogOpen}
        message={dialogMessage}
        title={dialogTitle}
      />
    </>
  );
}
export default CogsDialog;
